import { ArrowDown, ShoppingBag, Star, TrendingUp, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { currentFiscalYear } from "@/lib/fiscal";
import { cn } from "@/lib/utils";

interface FunnelStage {
  id: string;
  label: string;
  description: string;
  color: "blue" | "red";
  active: number;
}

interface FYTotals {
  opportunities: number;
  closedDeals: number;
  closeRate: number;
  grossSales: number;
  netSales: number;
}

interface JourneyData {
  fiscalYear: number;
  funnelStages: FunnelStage[];
  fy: FYTotals;
}

function fmt(n: number) {
  return new Intl.NumberFormat("en-US").format(n);
}
function fmtUSD(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(n);
}
function fmtPct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

const AVAILABLE_FYS = [currentFiscalYear(), currentFiscalYear() - 1, currentFiscalYear() - 2];

// Width percentages for the funnel taper effect (widest → narrowest)
const STAGE_WIDTHS = ["100%", "90%", "80%", "70%", "62%", "54%"];

export function ClientJourneyPage() {
  const [data, setData] = useState<JourneyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [fy, setFy] = useState(currentFiscalYear());

  const fetchData = useCallback(async (fiscalYear: number) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/client-journey?fiscalYear=${fiscalYear}`);
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(fy);
  }, [fy, fetchData]);

  const topCount = data?.funnelStages[0]?.active ?? 0;

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="FP Client Journey"
          description="Live pipeline health and client funnel from first inquiry to 6-star delivery."
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Fiscal Year</span>
          <select
            value={fy}
            onChange={(e) => setFy(Number(e.target.value))}
            className="border border-border rounded-md px-3 py-1.5 text-sm bg-background"
          >
            {AVAILABLE_FYS.map((y) => (
              <option key={y} value={y}>
                FY {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* FY Summary Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={`sum-skel-${i}`} className="rounded-lg border p-4 flex flex-col gap-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-7 w-20" />
            </div>
          ))
        ) : (
          <>
            <SummaryCard
              icon={Users}
              label="Opportunities"
              value={fmt(data?.fy.opportunities ?? 0)}
              sub={`FY ${fy}`}
              color="blue"
            />
            <SummaryCard
              icon={ShoppingBag}
              label="Closed Deals"
              value={fmt(data?.fy.closedDeals ?? 0)}
              sub={`FY ${fy}`}
              color="green"
            />
            <SummaryCard
              icon={TrendingUp}
              label="Close Rate"
              value={fmtPct(data?.fy.closeRate ?? 0)}
              sub={`FY ${fy}`}
              color="purple"
            />
            <SummaryCard
              icon={Star}
              label="Gross Sales"
              value={fmtUSD(data?.fy.grossSales ?? 0)}
              sub={`FY ${fy}`}
              color="gold"
            />
          </>
        )}
      </div>

      {/* Main Funnel */}
      <div className="flex flex-col items-center gap-0">
        <h2 className="text-lg font-semibold mb-4 self-start">
          Live Pipeline — Active Deals by Stage
        </h2>

        {loading ? (
          <div className="flex flex-col items-center gap-1 w-full max-w-2xl">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={`fs-${i}`}
                className="h-20 rounded-lg"
                style={{ width: STAGE_WIDTHS[i] }}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center w-full max-w-2xl gap-0.5">
            {data?.funnelStages.map((stage, i) => {
              const pct = topCount > 0 ? stage.active / topCount : 0;
              const prevStage = i > 0 ? data.funnelStages[i - 1] : null;
              const convFromPrev =
                prevStage && prevStage.active > 0 ? stage.active / prevStage.active : null;

              return (
                <div key={stage.id} className="flex flex-col items-center w-full">
                  {/* Drop arrow between stages */}
                  {i > 0 && (
                    <div className="flex items-center gap-2 my-0.5">
                      <ArrowDown className="h-4 w-4 text-muted-foreground/50" />
                      {convFromPrev !== null && (
                        <span
                          className={cn(
                            "text-xs font-medium",
                            convFromPrev >= 0.5
                              ? "text-green-600"
                              : convFromPrev >= 0.25
                                ? "text-amber-600"
                                : "text-red-500",
                          )}
                        >
                          {fmtPct(convFromPrev)} carried forward
                        </span>
                      )}
                    </div>
                  )}

                  {/* Stage block */}
                  <div
                    className={cn(
                      "flex items-center justify-between px-6 py-4 rounded-lg transition-all",
                      stage.color === "blue" ? "bg-blue-600 text-white" : "bg-rose-500 text-white",
                    )}
                    style={{ width: STAGE_WIDTHS[i] }}
                  >
                    {/* Left: step + description */}
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-xs font-medium opacity-80 uppercase tracking-wide">
                        Step {i + 1}
                      </span>
                      <span className="font-semibold text-sm leading-snug">{stage.label}</span>
                      <span className="text-xs opacity-75 leading-snug hidden sm:block">
                        {stage.description}
                      </span>
                    </div>

                    {/* Right: counts */}
                    <div className="flex flex-col items-end shrink-0 ml-4">
                      <span className="text-2xl font-bold tabular-nums">{fmt(stage.active)}</span>
                      <span className="text-xs opacity-75">
                        {topCount > 0 ? fmtPct(pct) : "—"} of top
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* 6 Star Client outcome */}
            <div className="flex flex-col items-center mt-2 gap-1">
              <ArrowDown className="h-5 w-5 text-muted-foreground" />
              <div className="flex items-center gap-3 bg-amber-50 border-2 border-amber-400 rounded-xl px-8 py-4 dark:bg-amber-900/20 dark:border-amber-500">
                <Star className="h-6 w-6 text-amber-500 fill-amber-400" />
                <div className="flex flex-col">
                  <span className="font-bold text-amber-800 dark:text-amber-300">
                    6 Star Client
                  </span>
                  <span className="text-xs text-amber-700 dark:text-amber-400">
                    {fmt(data?.fy.closedDeals ?? 0)} delivered this fiscal year
                  </span>
                </div>
                <Star className="h-6 w-6 text-amber-500 fill-amber-400" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Post-Purchase Section */}
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">After the Ring — Post-Purchase Journey</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <PostPurchaseSection
            title="Life Long Customers"
            color="blue"
            items={[
              "Customer Portal",
              "Created Bracelets",
              "FP Luxury",
              "Wedding Rings",
              "1 Year Anniversary Gift",
              "Mother's Day Gift",
            ]}
          />
          <PostPurchaseSection
            title="FP Walking Billboards"
            color="gold"
            items={["Google Reviews", "Affiliate Program", "Tag Us on Instagram"]}
          />
          <PostPurchaseSection
            title="Healthy Marriages"
            color="green"
            items={["FP Foundation", "On Marriage Book"]}
          />
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub: string;
  color: "blue" | "green" | "purple" | "gold";
}) {
  const iconClass = {
    blue: "text-blue-500 bg-blue-50 dark:bg-blue-900/20",
    green: "text-green-600 bg-green-50 dark:bg-green-900/20",
    purple: "text-purple-600 bg-purple-50 dark:bg-purple-900/20",
    gold: "text-amber-600 bg-amber-50 dark:bg-amber-900/20",
  }[color];

  return (
    <div className="rounded-lg border p-4 flex flex-col gap-3">
      <div className={cn("h-8 w-8 rounded-md flex items-center justify-center", iconClass)}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-xl font-bold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">
          {label} · {sub}
        </div>
      </div>
    </div>
  );
}

function PostPurchaseSection({
  title,
  color,
  items,
}: {
  title: string;
  color: "blue" | "gold" | "green";
  items: string[];
}) {
  const headerClass = {
    blue: "bg-blue-600 text-white",
    gold: "bg-amber-500 text-white",
    green: "bg-green-600 text-white",
  }[color];

  const dotClass = {
    blue: "bg-blue-400",
    gold: "bg-amber-400",
    green: "bg-green-400",
  }[color];

  return (
    <div className="rounded-lg border overflow-hidden">
      <div className={cn("px-4 py-3 font-semibold text-sm", headerClass)}>{title}</div>
      <div className="divide-y">
        {items.map((item) => (
          <div key={item} className="flex items-center gap-3 px-4 py-2.5">
            <div className={cn("h-2 w-2 rounded-full shrink-0", dotClass)} />
            <span className="text-sm">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
